import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Button from "../components/utilComponents/Button";
import {
  useFetchOrderQuery,
  useInitiatePaymentMutation,
} from "../slices/checkoutSlice";
import Success from "../components/message/Success";
import Error from "../components/message/Error";
import Loader from "../components/utilComponents/Loader";

const OrderReview = () => {
  const { orderId } = useParams();
  const {
    data: order,
    isLoading,
    error: fetchError,
  } = useFetchOrderQuery(orderId);

  const [initiatePayment] = useInitiatePaymentMutation();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handlePlaceOrder = async () => {
    try {
      if (!order) return setErrorMessage("Order details not found.");

      if (!order.vendorSubaccounts?.length) {
        return setErrorMessage("No vendor subaccounts found.");
      }

      const subaccounts = order.vendorSubaccounts.map((vendor) => ({
        id: vendor.subaccountId,
        transaction_charge_type: "flat_subaccount",
        transaction_charge: vendor.payoutAmount,
      }));

      const paymentData = {
        tx_ref: orderId,
        amount: order.total,
        currency: "NGN",
        redirect_url: `${window.location.origin}/checkout/confirmation/${orderId}`,
        customer: {
          email: order.shippingInfo.email,
          phonenumber: order.shippingInfo.phoneNumber,
          name: `${order.shippingInfo.firstName} ${order.shippingInfo.lastName}`,
        },
        customizations: {
          title: `Agrolinka Checkout #${orderId}`,
          description: `Payment for Order #${orderId}`,
          logo: "https://res.cloudinary.com/agrolinka-com/image/upload/v1744820790/flutter_agrolinka_icon_jorwub.png",
        },
        subaccounts,
      };

      const response = await initiatePayment(paymentData).unwrap();
      if (response.status === "success") {
        window.location.href = response.data.link;
      }
    } catch (error) {
      setErrorMessage(error?.data?.message || "Failed to initiate payment.");
    }
  };

  if (isLoading) {
    return (
      <div>
        <div className="md:hidden mx-2">
          <Loader count={1} columns={1} itemClass="h-[320px]" />
          <Loader count={1} columns={1} itemClass="h-[200px]" />
        </div>
        <div className="hidden md:block container">
          <Loader count={2} columns={2} itemClass="h-[320px]" />
        </div>
      </div>
    );
  }

  if (fetchError) return <Error message="Failed to fetch order details." />;

  return (
    <div>
      {/* Desktop View */}
      <div className="hidden lg:flex m-2 md:container flex flex-col space-y-4 md:flex-row md:space-x-8 md:space-y-0">
        {/* Order Details Section */}
        <div className="flex-4 order-summary">
          <h3 className="font-nunito text-agBlack font-semibold text-lg mb-4">
            Review Your Order
          </h3>
          <div className="space-y-4">
            <h4 className="font-medium text-lg mb-4">Order Items</h4>
            {order.orderItems.map((item) => (
              <div
                key={item.productId}
                // className="flex items-center space-x-4 shadow-md p-4 rounded-lg"
                className="flex items-center space-x-4 border-b pb-4"
              >
                <img
                  src={item.productImage || "/placeholder.jpg"}
                  alt={item.productTitle}
                  className="w-20 h-20 object-cover rounded-md"
                />
                <div className="flex-1">
                  <p className="font-nunito text-agBlack text-sm">
                    {item.productTitle || "Untitled Product"}
                  </p>
                  <p className="text-xs text-agLightGray">
                    Qty: {item.qty} × ₦{item.price.toLocaleString("en-NG")}
                  </p>
                </div>
                <p className="text-sm font-semibold text-agBlack">
                  ₦{(item.qty * item.price).toLocaleString("en-NG")}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <h4 className="font-semibold">Shipping Information</h4>
            <p className="text-sm text-agLightGray">
              {order.shippingInfo.firstName} {order.shippingInfo.lastName}
            </p>
            <p className="text-sm text-agLightGray">
              {order.shippingInfo.address}
            </p>
            <p className="text-sm text-agLightGray">
              {order.shippingInfo.phoneNumber}
            </p>
          </div>

          {/* <div>
            <h4 className="font-semibold">Order Items</h4>
            {order.orderItems.map((item) => (
              <div key={item.item} className="mb-2">
                <p className="text-sm">{item.itemTitle}</p>
                <p className="text-xs text-agLightGray">
                  {item.qty} x ₦{item.price.toLocaleString()}
                </p>
              </div>
            ))}
          </div> */}
        </div>

        {/* Order Summary Section */}
        <div className="flex-2 w-full lg:w-[30vw]">
          <div className="order-summary">
            <h3 className="font-nunito text-agBlack font-semibold text-lg">
              Order Summary
            </h3>
            <hr className="mb-4" />
            <div className="order-summary-details">
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">Subtotal</span>
                <span className="text-sm text-agBlack">
                  ₦{order.subTotal.toLocaleString("en-NG")}
                </span>
              </div>
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">Shipping</span>
                <span className="text-sm text-agBlack">
                  ₦{order.shipping.toLocaleString("en-NG")}
                </span>
              </div>
              <div className="order-summary-item">
                <span className="text-sm text-agLightGray">
                  Tax ({order.taxRate}%)
                </span>
                <span className="text-sm text-agBlack">
                  ₦{order.tax.toLocaleString("en-NG")}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Total</span>
                <span className="text-agBlack font-nunito font-bold text-lg">
                  ₦{order.total.toLocaleString("en-NG")}
                </span>
              </div>
            </div>
            <Button variant="primary" fullWidth onClick={handlePlaceOrder}>
              PLACE ORDER
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden m-2 flex flex-col space-y-4">
        <div>
          <h3 className="font-nunito text-agBlack font-semibold text-lg mb-4 mx-2">
            Order Review
          </h3>
          {/* Order Items */}
          <div className="space-y-4">
            <h4 className="font-semibold text-lg mb-2 mx-2">Order Items</h4>
            {order.orderItems.map((item) => (
              <div
                key={item.productId}
                className="flex items-center space-x-4 border-b pb-4 mx-2"
              >
                <img
                  src={item.productImage || "/placeholder.jpg"}
                  alt={item.productTitle}
                  className="w-20 h-20 object-cover rounded-md"
                />
                <div className="flex-1">
                  <p className="font-nunito text-agBlack text-sm">
                    {item.productTitle || "Untitled Product"}
                  </p>
                  <p className="text-xs text-agLightGray">
                    Qty: {item.qty} × ₦{item.price.toLocaleString("en-NG")}
                  </p>
                </div>
                <p className="text-sm font-semibold text-agBlack">
                  ₦{(item.qty * item.price).toLocaleString("en-NG")}
                </p>
              </div>
            ))}
          </div>
          {/* <h4 className="mt-4 font-semibold">Order Items</h4>
          {order.orderItems.map((item) => (
            <div key={item.item} className="mb-2">
              <p className="text-sm">{item.itemTitle}</p>
              <p className="text-xs text-agLightGray">
                {item.qty} x ₦{item.price.toLocaleString()}
              </p>
            </div>
          ))} */}
          <h4 className="font-semibold mt-6 mx-2">Shipping Information</h4>
          <p className="text-sm text-agLightGray mx-2">
            {order.shippingInfo.firstName} {order.shippingInfo.lastName}
          </p>
          <p className="text-sm text-agLightGray mx-2">
            {order.shippingInfo.address}
          </p>
          <p className="text-sm text-agLightGray mx-2">
            {order.shippingInfo.phoneNumber}
          </p>
        </div>
        {/* Order Summary Section */}

        <div className="order-summary">
          <h3 className="font-nunito text-agBlack font-semibold text-lg">
            Order Summary
          </h3>
          <hr className="mb-4" />
          <div className="order-summary-details">
            <div className="order-summary-item">
              <span className="text-sm text-agLightGray">Subtotal</span>
              <span className="text-sm text-agBlack">
                ₦{order.subTotal.toLocaleString("en-NG")}
              </span>
            </div>
            <div className="order-summary-item">
              <span className="text-sm text-agLightGray">Shipping</span>
              <span className="text-sm text-agBlack">
                ₦{order.shipping.toLocaleString("en-NG")}
              </span>
            </div>
            <div className="order-summary-item">
              <span className="text-sm text-agLightGray">
                Tax ({order.taxRate}%)
              </span>
              <span className="text-sm text-agBlack">
                ₦{order.tax.toLocaleString("en-NG")}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Total</span>
              <span className="text-agBlack font-nunito font-bold text-lg">
                ₦{order.total.toLocaleString("en-NG")}
              </span>
            </div>
          </div>
          <Button variant="primary" fullWidth onClick={handlePlaceOrder}>
            PLACE ORDER
          </Button>
        </div>
        {successMessage && (
          <Success
            message={successMessage}
            onClose={() => setSuccessMessage("")}
          />
        )}
        {errorMessage && (
          <Error message={errorMessage} onClose={() => setErrorMessage("")} />
        )}
      </div>
    </div>
  );
};

export default OrderReview;

// import React, { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import Button from "../components/utilComponents/Button";
// import {
//   useFetchOrderQuery,
//   useInitiatePaymentMutation,
// } from "../slices/checkoutSlice";
// import Success from "../components/message/Success";
// import Error from "../components/message/Error";

// const OrderReview = () => {
//   const { orderId } = useParams();
//   const {
//     data: order,
//     isLoading,
//     error: fetchError,
//   } = useFetchOrderQuery(orderId);
//   const [initiatePayment] = useInitiatePaymentMutation();
//   const [successMessage, setSuccessMessage] = useState("");
//   const [errorMessage, setErrorMessage] = useState("");

//   const handlePlaceOrder = async () => {
//     try {
//       if (!order) {
//         setErrorMessage("Order details not found.");
//         return;
//       }

//       console.log("Fetched Order Data:", order); // Debugging

//       if (!order.vendorSubaccounts || order.vendorSubaccounts.length === 0) {
//         console.error("Vendor Subaccounts Missing:", order.vendorSubaccounts);
//         setErrorMessage("No vendor subaccounts found.");
//         return;
//       }

//       const subaccounts = order.vendorSubaccounts.map((vendor) => ({
//         id: vendor.subaccountId,
//         transaction_charge_type: "flat_subaccount", // ✅ Use exact payout
//         transaction_charge: vendor.payoutAmount, // ✅ Vendor gets this exact amount
//       }));

//       const paymentData = {
//         tx_ref: orderId,
//         amount: order.total,
//         currency: "NGN",
//         redirect_url: `${window.location.origin}/checkout/confirmation/${orderId}`,
//         customer: {
//           email: order.shippingInfo.email,
//           phonenumber: order.shippingInfo.phoneNumber,
//           name: `${order.shippingInfo.firstName} ${order.shippingInfo.lastName}`,
//         },
//         customizations: {
//           title: `Payment for Order #${orderId}`,
//           description: `Payment for Order #${orderId}`,
//           logo: "https://res.cloudinary.com/agrolinka-com/image/upload/v1744820790/flutter_agrolinka_icon_jorwub.png",
//         },
//         subaccounts,
//       };

//       console.log(
//         "Final Payment Payload:",
//         JSON.stringify(paymentData, null, 2),
//       );
//       console.log("SUBACCOUNTS", paymentData.subaccounts);

//       const response = await initiatePayment(paymentData).unwrap();
//       if (response.status === "success") {
//         window.location.href = response.data.link;
//       }
//     } catch (error) {
//       setErrorMessage(error?.data?.message || "Failed to initiate payment.");
//     }
//   };

//   if (isLoading) return <div>Loading...</div>;
//   if (fetchError) return <Error message="Failed to fetch order details." />;

//   return (
//     <div className="checkout-container">
//       <h3>Review Your Order</h3>
//       <div className="order-summary">
//         <h4>Shipping Information</h4>
//         <p>
//           {order.shippingInfo.firstName} {order.shippingInfo.lastName}
//         </p>
//         <p>{order.shippingInfo.address}</p>
//         <p>{order.shippingInfo.phoneNumber}</p>

//         <h4>Order Items</h4>
//         {order.orderItems.map((item) => (
//           <div key={item.item}>
//             <p>{item.itemTitle}</p>
//             <p>
//               {item.qty} x ₦{item.price.toLocaleString()}
//             </p>
//           </div>
//         ))}
//         <p>Subtotal: ₦{order.subTotal.toLocaleString()}</p>
//         <p>Shipping: ₦{order.shipping.toLocaleString()}</p>
//         <p>
//           Tax ({order.taxRate}%): ₦{order.tax.toLocaleString()}
//         </p>
//         <p>Total: ₦{order.total.toLocaleString()}</p>
//       </div>

//       <Button
//         variant="primary"
//         onClick={handlePlaceOrder}
//         disabled={isLoading}
//         fullWidth
//       >
//         PLACE ORDER
//       </Button>

//       {successMessage && (
//         <Success
//           message={successMessage}
//           onClose={() => setSuccessMessage("")}
//         />
//       )}
//       {errorMessage && (
//         <Error message={errorMessage} onClose={() => setErrorMessage("")} />
//       )}
//     </div>
//   );
// };

// export default OrderReview;
